import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  message,
} from "antd";
import type { TableColumnsType } from "antd";
import { AdminPageShell } from "../components/admin/AdminPageShell";
import { fetchAdminPlans, patchAdminPlan } from "../api/client";
import type { AdminPlanRow } from "../api/types";

function fmtUsd(cents: number) {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

export function BillingPlansPage() {
  const [items, setItems] = useState<AdminPlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<"forbidden" | "error" | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<AdminPlanRow | null>(null);
  const [form] = Form.useForm<{
    name: string;
    billing_cycle: string;
    price_usd: number;
    scan_quota: number | null;
    is_unlimited: boolean;
    is_active: boolean;
    sort_order: number;
  }>();

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const r = await fetchAdminPlans();
    if (r === "forbidden") setErr("forbidden");
    else if (r === "error") setErr("error");
    else setItems(r.items);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openEdit = (row: AdminPlanRow) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      billing_cycle: row.billing_cycle,
      price_usd: row.price_usd_cents / 100,
      scan_quota: row.scan_quota ?? null,
      is_unlimited: row.is_unlimited,
      is_active: row.is_active,
      sort_order: row.sort_order,
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      const v = await form.validateFields();
      setSaving(true);
      const body: Parameters<typeof patchAdminPlan>[1] = {
        name: v.name.trim(),
        billing_cycle: v.billing_cycle.trim(),
        price_usd_cents: Math.max(0, Math.round(Number(v.price_usd) * 100)),
        is_unlimited: v.is_unlimited,
        is_active: v.is_active,
        sort_order: Number(v.sort_order),
      };
      if (!v.is_unlimited) {
        const q = v.scan_quota;
        body.scan_quota =
          q == null || (typeof q === "number" && Number.isNaN(q))
            ? 0
            : Math.max(0, Math.floor(Number(q)));
      }
      const r = await patchAdminPlan(editing.id, body);
      setSaving(false);
      if (r === "forbidden") {
        message.error("无权限");
        return;
      }
      if (r === "error") {
        message.error("保存失败");
        return;
      }
      message.success("已保存");
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch {
      setSaving(false);
    }
  };

  const columns: TableColumnsType<AdminPlanRow> = [
    {
      title: "排序",
      dataIndex: "sort_order",
      key: "sort_order",
      width: 72,
    },
    {
      title: "代码",
      dataIndex: "code",
      key: "code",
      width: 160,
      render: (c: string) => <span className="admin-ledger-mono">{c}</span>,
    },
    {
      title: "名称",
      dataIndex: "name",
      key: "name",
      width: 200,
      ellipsis: true,
    },
    {
      title: "周期",
      dataIndex: "billing_cycle",
      key: "billing_cycle",
      width: 100,
    },
    {
      title: "价格 (USD)",
      dataIndex: "price_usd_cents",
      key: "price_usd_cents",
      width: 120,
      render: (c: number) => <span className="admin-ledger-num">{fmtUsd(c)}</span>,
    },
    {
      title: "扫描额度",
      dataIndex: "scan_quota",
      key: "scan_quota",
      width: 100,
      render: (q: number | null, row) =>
        row.is_unlimited ? (
          <Tag>不限</Tag>
        ) : (
          <span>{q != null ? q.toLocaleString("zh-CN") : "—"}</span>
        ),
    },
    {
      title: "上架",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (a: boolean) =>
        a ? <Tag color="success">是</Tag> : <Tag>否</Tag>,
    },
    {
      title: "操作",
      key: "op",
      width: 88,
      fixed: "right",
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => openEdit(row)}>
          编辑
        </Button>
      ),
    },
  ];

  if (err === "forbidden") {
    return (
      <AdminPageShell title="订阅计划" description="维护对外售价与展示（改价后前台下单按新价）。">
        <Alert type="error" showIcon message="无权限访问管理接口" />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="订阅计划"
      description="维护套餐名称、标价（美元）、计费周期、扫描额度与上架状态。code / id 由数据迁移固定，此处不可改。"
    >
      {err === "error" ? (
        <Alert
          type="error"
          showIcon
          message="加载失败"
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <div className="admin-ledger-table-wrap">
        <Spin spinning={loading}>
          <Table<AdminPlanRow>
            rowKey="id"
            className="admin-ledger-table"
            columns={columns}
            dataSource={items}
            pagination={false}
            scroll={{ x: 1100 }}
            locale={{ emptyText: "暂无套餐数据" }}
          />
        </Spin>
      </div>

      <Modal
        title={editing ? `编辑 · ${editing.code}` : "编辑套餐"}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditing(null);
        }}
        onOk={() => void submitEdit()}
        confirmLoading={saving}
        destroyOnHidden
        width={480}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 8 }}
        >
          <Form.Item
            name="name"
            label="展示名称"
            rules={[{ required: true, message: "请输入名称" }]}
          >
            <Input placeholder="套餐名称" />
          </Form.Item>
          <Form.Item
            name="price_usd"
            label="价格（美元）"
            rules={[{ required: true, message: "请输入价格" }]}
          >
            <InputNumber
              min={0}
              precision={2}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="如 9.99"
            />
          </Form.Item>
          <Form.Item
            name="billing_cycle"
            label="计费周期（如 monthly / yearly）"
            rules={[{ required: true, message: "请输入周期" }]}
          >
            <Input placeholder="monthly" />
          </Form.Item>
          <Form.Item name="is_unlimited" label="无限额度" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item noStyle dependencies={["is_unlimited"]}>
            {() =>
              form.getFieldValue("is_unlimited") ? null : (
                <Form.Item name="scan_quota" label="每月扫描次数额度">
                  <InputNumber min={0} precision={0} style={{ width: "100%" }} placeholder="整数" />
                </Form.Item>
              )}
          </Form.Item>
          <Form.Item name="is_active" label="上架（前台 /plans 可见）" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="排序（越小越靠前）"
            rules={[{ required: true, message: "请输入排序" }]}
          >
            <InputNumber precision={0} style={{ width: "100%" }} />
          </Form.Item>
          {editing ? (
            <p className="admin-ledger-desc" style={{ marginBottom: 0 }}>
              <Space direction="vertical" size={4}>
                <span>
                  代码 <code>{editing.code}</code> · id{" "}
                  <code className="admin-ledger-mono">{editing.id.slice(0, 8)}…</code>
                </span>
                <span className="admin-ledger-desc">
                  已生成订单金额仍以创建时为准；新单按当前标价。
                </span>
              </Space>
            </p>
          ) : null}
        </Form>
      </Modal>
    </AdminPageShell>
  );
}
